import { find, map } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Campaign, CampaignType } from 'src/actions/campaigns/campaignsResource';
import recipientCollectionsResource from 'src/actions/campaigns/recipientCollectionsResource';
import { toastError } from 'src/actions/toast';
import useResource, { getConsolidatedState, someFailed, STATUS_FAILED } from 'src/presentation/hooks/useResource';
import { toDateTimeString } from 'src/presentation/components/pages/shared/utils/TimeUtils';
import useProactiveCommunication from './useProactiveCommunication';

type UseRecipientCollectionsType = { campaignId: string; campaignType: CampaignType };

const useRecipientCollections = ({ campaignId, campaignType }: UseRecipientCollectionsType) => {
  const dispatch = useDispatch();

  const { campaigns, loadingState: campaignLoadingState } = useProactiveCommunication({ campaignType });

  const { loadingState: recipientCollectionsLoadingState, values: collectionsValues } = useResource(
    recipientCollectionsResource,
    { fetch: true, params: { campaignId } }
  );

  const loadingStates = [recipientCollectionsLoadingState, campaignLoadingState];
  const fetchError = someFailed(...loadingStates);
  const loadingState = fetchError ? STATUS_FAILED : getConsolidatedState(loadingStates);

  const campaign = useMemo(
    () => find(campaigns, (campaign: Campaign) => campaign.id === campaignId),
    [campaigns, campaignId]
  );

  const recipientCollectionsValues = useMemo(
    () =>
      map(collectionsValues, value => ({
        ...value,
        createdAtString: toDateTimeString(value.createdAt),
        progressString: `${value.completedRecipients}/${value.totalRecipients}`,
      })),
    [collectionsValues]
  );

  useEffect(() => {
    if (fetchError) {
      dispatch(toastError('Error while loading data. Please wait and try refreshing the page'));
    }
  }, [fetchError, dispatch]);

  return { campaign, recipientCollections: recipientCollectionsValues, loadingState };
};

export default useRecipientCollections;
